/**
 * ClientProfile — client can view and update their name and phone number.
 *
 * Phone number is used by the voice agent to recognise the caller when they
 * ring the agent's Twilio number, so the AI knows who is calling and can
 * pull up their dossier automatically.
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  profilePhotoService,
  uploadProfilePhotoFile,
} from "@re-platform/api-client";
import { formatPhoneUS, PhoneInput } from "@re-platform/ui";
import { api } from "../../lib/api";
import { loadSession } from "../auth/session";
import { ManagePasskeys } from "./ManagePasskeys";

const PHOTO_ALLOWED_EXT = ["png", "jpg", "jpeg", "webp"];
const PHOTO_MAX_BYTES = 1_048_576;

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  phone_e164: string | null;
  primary_role: string;
  photo_url: string | null;
}

export function ClientProfile() {
  const qc = useQueryClient();
  const session = loadSession();

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [editing, setEditing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const profileQ = useQuery({
    queryKey: ["profile", "me"],
    queryFn: () => api.request<ProfileData>("/profile/me"),
  });

  // Sync fetched values into the form fields (react-query v5 removed the
  // useQuery onSuccess callback). Skip while the user is actively editing.
  useEffect(() => {
    if (profileQ.data && !editing) {
      setFullName(profileQ.data.full_name ?? "");
      setPhone(profileQ.data.phone_e164 ?? "");
    }
  }, [profileQ.data, editing]);

  const patch = useMutation({
    mutationFn: (body: {
      full_name?: string | null;
      phone_e164?: string | null;
    }) => api.request("/profile/me", { method: "PATCH", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile", "me"] });
      setEditing(false);
      showToast("Profile saved.", true);
    },
    onError: () => showToast("Failed to save. Please try again.", false),
  });

  const photoInputRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      if (!PHOTO_ALLOWED_EXT.includes(ext)) {
        throw new Error(
          `Unsupported file type — allowed: ${PHOTO_ALLOWED_EXT.join(", ")}`,
        );
      }
      if (file.size > PHOTO_MAX_BYTES) {
        throw new Error(`Photo exceeds the ${PHOTO_MAX_BYTES / 1024}KB limit`);
      }
      const presigned = await profilePhotoService.presignUpload(api, ext);
      await uploadProfilePhotoFile(
        presigned.upload_url,
        presigned.content_type,
        file,
      );
      return profilePhotoService.confirmUpload<ProfileData>(
        api,
        presigned.s3_key,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profile", "me"] });
      showToast("Photo uploaded.", true);
    },
    onError: (err: Error) => showToast(err.message, false),
  });

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  const d = profileQ.data;
  const initial = (
    (d?.full_name ?? session?.displayName ?? "?")[0] ?? "?"
  ).toUpperCase();

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "24px 16px" }}>
      <h1 style={{ marginTop: 0, fontSize: "1.4rem" }}>My profile</h1>

      {toast && (
        <div
          style={{
            marginBottom: 16,
            padding: "10px 16px",
            background: toast.ok
              ? "var(--rp-color-success-faint)"
              : "var(--rp-color-danger-bg)",
            border: `1px solid ${toast.ok ? "var(--rp-color-success)" : "var(--rp-color-danger)"}`,
            borderRadius: 8,
            color: toast.ok
              ? "var(--rp-color-success)"
              : "var(--rp-color-danger)",
            fontSize: "0.875rem",
          }}
        >
          {toast.msg}
        </div>
      )}

      <div
        style={{
          background: "var(--rp-color-surface, white)",
          border: "1px solid var(--rp-color-border, #e2e8f0)",
          borderRadius: 12,
          padding: "24px",
        }}
      >
        {!profileQ.isLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "var(--rp-color-primary, #0369a1)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
                fontWeight: 700,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {d?.photo_url ? (
                <img
                  src={d.photo_url}
                  alt={d?.full_name ?? "Profile photo"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                initial
              )}
            </div>
            <div>
              <input
                ref={photoInputRef}
                type="file"
                accept={PHOTO_ALLOWED_EXT.map((e) => `.${e}`).join(",")}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadPhoto.mutate(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="rp-btn rp-btn--sm"
                disabled={uploadPhoto.isPending}
                onClick={() => photoInputRef.current?.click()}
              >
                {uploadPhoto.isPending
                  ? "Uploading…"
                  : d?.photo_url
                    ? "Replace photo"
                    : "Add photo"}
              </button>
            </div>
          </div>
        )}

        {profileQ.isLoading ? (
          <p style={{ color: "var(--rp-color-text-muted)", margin: 0 }}>
            Loading…
          </p>
        ) : editing ? (
          /* Edit form */
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "var(--rp-color-text-muted)",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Full name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your name"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--rp-color-border, #e2e8f0)",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "var(--rp-color-text-muted)",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Phone number
              </label>
              {/* Stores E.164, shows "(512) 555-0142". This number is the key
                  the voice agent matches an inbound caller on, so a typo here
                  means the agent does not recognise them on the phone. */}
              <PhoneInput
                className=""
                value={phone}
                onChange={(e164) => setPhone(e164 ?? "")}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--rp-color-border, #e2e8f0)",
                  fontSize: "0.95rem",
                  boxSizing: "border-box",
                }}
              />
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: "0.78rem",
                  color: "var(--rp-color-text-muted)",
                }}
              >
                Used to identify you when you call your agent's AI number.
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button
                type="button"
                className="rp-btn rp-btn--primary rp-btn--sm"
                disabled={patch.isPending}
                onClick={() =>
                  patch.mutate({
                    full_name: fullName || null,
                    phone_e164: phone || null,
                  })
                }
              >
                {patch.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="rp-btn rp-btn--sm"
                onClick={() => {
                  setEditing(false);
                  setFullName(d?.full_name ?? "");
                  setPhone(d?.phone_e164 ?? "");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* Read view */
          <div>
            <Row
              label="Name"
              value={d?.full_name ?? session?.displayName ?? "—"}
            />
            <Row label="Email" value={d?.email ?? session?.email ?? "—"} />
            <Row
              label="Phone"
              value={
                (d?.phone_e164 ? formatPhoneUS(d.phone_e164) : null) ?? (
                  <span
                    style={{
                      color: "var(--rp-color-text-muted)",
                      fontSize: "0.875rem",
                    }}
                  >
                    Not set
                  </span>
                )
              }
            />
            <div style={{ marginTop: 20 }}>
              <button
                type="button"
                className="rp-btn rp-btn--sm"
                onClick={() => setEditing(true)}
              >
                Edit profile
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Voice agent tip */}
      {!profileQ.isLoading && !d?.phone_e164 && (
        <div
          style={{
            marginTop: 16,
            padding: "12px 16px",
            background: "var(--rp-color-warning-faint)",
            border: "1px solid var(--rp-color-warning)",
            borderRadius: 8,
            fontSize: "0.85rem",
            color: "var(--rp-color-warning-dark)",
          }}
        >
          <strong>Tip:</strong> Add your phone number so that when you call your
          agent's AI line, it recognises you automatically and pulls up your
          file.
        </div>
      )}

      {/* Manage passkeys — the profile half of V072. Until this shipped a
          client could hold exactly ONE passkey and had no way to add another. */}
      <ManagePasskeys />
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid var(--rp-color-border, #e2e8f0)",
        gap: 8,
      }}
    >
      <span
        style={{
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "var(--rp-color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          flexShrink: 0,
          width: 80,
        }}
      >
        {label}
      </span>
      {/* A flex item's min-width defaults to AUTO, not 0 — so a long unbroken value
          (an email address has no spaces to break at) refuses to shrink and runs
          straight out of the frame on a phone. min-width:0 lets it shrink, and
          overflow-wrap lets it break mid-word once it has to. */}
      <span
        style={{
          fontWeight: 600,
          textAlign: "right",
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </span>
    </div>
  );
}
